import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Siren, X, HeartPulse, Flame, Wrench, ShieldQuestion, CircleHelp, MapPin, ChevronDown } from 'lucide-react';
import { getSosEligibility, raiseSos, cancelSos } from '@/api/booking';
import type { SosCategory } from '@/api/booking';

const F = "'Figtree', sans-serif";

const CATEGORIES: { key: SosCategory; label: string; icon: React.ElementType }[] = [
  { key: 'medical', label: 'Medical', icon: HeartPulse },
  { key: 'security', label: 'Security / Safety', icon: ShieldQuestion },
  { key: 'fire_gas', label: 'Fire / Gas', icon: Flame },
  { key: 'maintenance', label: 'Maintenance', icon: Wrench },
  { key: 'other', label: 'Other', icon: CircleHelp },
];

const CATEGORY_LABEL: Record<SosCategory, string> = {
  medical: 'Medical',
  security: 'Security / Safety',
  fire_gas: 'Fire / Gas',
  maintenance: 'Urgent maintenance',
  other: 'Other emergency',
};

const STYLE_ID = 'sos-widget-keyframes';
function useSosKeyframes() {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = `
      @keyframes sosPulse { 0%{box-shadow:0 0 0 0 rgba(192,57,43,.5)} 70%{box-shadow:0 0 0 8px rgba(192,57,43,0)} 100%{box-shadow:0 0 0 0 rgba(192,57,43,0)} }
      @keyframes sosPop { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:none} }
    `;
    document.head.appendChild(el);
  }, []);
}

// ── Raise modal ───────────────────────────────────────────────────────────────

function SosModal({ location, onClose }: { location: string | null; onClose: () => void }) {
  const [category, setCategory] = useState<SosCategory | null>(null);
  const [message, setMessage] = useState('');
  const queryClient = useQueryClient();

  const raiseMutation = useMutation({
    mutationFn: () => raiseSos({ category: category!, message: message.trim() || undefined }),
    onSuccess: () => {
      toast.success('SOS sent — responders notified');
      queryClient.invalidateQueries({ queryKey: ['sos', 'status'] });
      onClose();
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to send SOS'),
  });

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(15,20,35,0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget && !raiseMutation.isPending) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 22, maxWidth: 460, width: '100%', overflow: 'hidden', boxShadow: '0 30px 60px rgba(0,0,0,.25)', animation: 'sosPop .22s ease both', fontFamily: F }}>
        {/* Red header */}
        <div style={{ background: 'linear-gradient(135deg, #c0392b, #a52c1f)', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 13 }}>
          <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(255,255,255,0.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Siren size={22} />
          </div>
          <div>
            <h3 style={{ font: `800 20px ${F}`, color: '#fff', margin: 0 }}>Raise an emergency SOS</h3>
            <p style={{ font: `500 12.5px ${F}`, color: 'rgba(255,255,255,0.85)', margin: '2px 0 0' }}>Responders get your name, phone and booking location.</p>
          </div>
        </div>

        <div style={{ padding: '20px 24px 22px' }}>
          {location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, font: `600 12.5px ${F}`, color: '#5c564a', background: '#faf8f3', border: '1px solid #f0ece2', borderRadius: 10, padding: '9px 12px', marginBottom: 16 }}>
              <MapPin size={14} style={{ color: '#c0392b', flexShrink: 0 }} />
              <span>Your location: <strong style={{ color: '#1B2B4B' }}>{location}</strong></span>
            </div>
          )}

          <div style={{ font: `700 11px ${F}`, color: '#7A8BA8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 9 }}>
            What's happening?
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))', gap: 8, marginBottom: 18 }}>
            {CATEGORIES.map(({ key, label, icon: Icon }) => {
              const active = category === key;
              return (
                <button key={key} type="button" onClick={() => setCategory(key)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 7, padding: '12px 13px', borderRadius: 12,
                    border: active ? '1.5px solid #c0392b' : '1.5px solid #E8EDF5',
                    background: active ? '#fdecea' : '#fff', color: active ? '#c0392b' : '#5c564a',
                    font: `600 13px ${F}`, cursor: 'pointer', transition: 'all .14s', textAlign: 'left',
                  }}>
                  <Icon size={18} style={{ flexShrink: 0 }} /> {label}
                </button>
              );
            })}
          </div>

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={1000}
            placeholder="Add any detail that helps responders (optional)"
            rows={3}
            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', borderRadius: 12, border: '1.5px solid #E8EDF5', padding: '11px 13px', font: `400 13.5px ${F}`, color: '#1B2B4B', marginBottom: 20, outline: 'none' }}
          />

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={raiseMutation.isPending}
              style={{ font: `600 13.5px ${F}`, color: '#5c564a', background: '#f0ece2', border: 'none', borderRadius: 11, padding: '11px 22px', cursor: raiseMutation.isPending ? 'not-allowed' : 'pointer', opacity: raiseMutation.isPending ? 0.6 : 1 }}>
              Cancel
            </button>
            <button type="button" onClick={() => raiseMutation.mutate()} disabled={!category || raiseMutation.isPending}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, font: `700 13.5px ${F}`, color: '#fff', background: '#c0392b', border: 'none', borderRadius: 11, padding: '11px 24px', cursor: (!category || raiseMutation.isPending) ? 'not-allowed' : 'pointer', opacity: (!category || raiseMutation.isPending) ? 0.55 : 1 }}>
              <Siren size={15} /> {raiseMutation.isPending ? 'Sending…' : 'Send SOS'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Active-alert dropdown ─────────────────────────────────────────────────────

function ActivePill({ alert, onCancel, cancelling }: {
  alert: { id: number; status: string; category: SosCategory; location_label: string | null; acknowledged_by_name: string | null };
  onCancel: () => void; cancelling: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const acknowledged = alert.status === 'acknowledged';

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, font: `700 13px ${F}`, color: '#fff',
          background: acknowledged ? '#a4530f' : '#c0392b', border: 'none', borderRadius: 20,
          padding: '6px 13px', cursor: 'pointer', animation: acknowledged ? 'none' : 'sosPulse 2s infinite',
        }}>
        <Siren size={15} /> {acknowledged ? 'Help on the way' : 'SOS active'}
        <ChevronDown size={13} style={{ opacity: 0.8 }} />
      </button>

      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 288, background: '#fff', border: '1px solid #E8EDF5', borderRadius: 14, boxShadow: '0 12px 32px rgba(0,0,0,.16)', zIndex: 60, overflow: 'hidden', animation: 'sosPop .18s ease both' }}>
          <div style={{ padding: '14px 16px', background: acknowledged ? '#fdeedd' : '#fdecea', borderBottom: '1px solid #f0ece2' }}>
            <div style={{ font: `800 14px ${F}`, color: acknowledged ? '#8a5a12' : '#8a2a1e' }}>
              {acknowledged ? 'Help is on the way' : 'SOS sent'}
            </div>
            <div style={{ font: `500 12px ${F}`, color: acknowledged ? '#a4530f' : '#a4443a', marginTop: 2 }}>
              {acknowledged
                ? `${alert.acknowledged_by_name ?? 'A responder'} is coordinating help.`
                : 'Responders have been notified. Keep your phone reachable.'}
            </div>
          </div>
          <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ font: `600 12.5px ${F}`, color: '#1B2B4B' }}>{CATEGORY_LABEL[alert.category]}</div>
            {alert.location_label && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, font: `400 12.5px ${F}`, color: '#7A8BA8' }}>
                <MapPin size={12} /> {alert.location_label}
              </div>
            )}
          </div>
          <button type="button" onClick={onCancel} disabled={cancelling}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 16px', background: '#f0ece2', border: 'none', borderTop: '1px solid #E8EDF5', font: `600 12.5px ${F}`, color: '#c0392b', cursor: cancelling ? 'not-allowed' : 'pointer', opacity: cancelling ? 0.6 : 1 }}>
            <X size={13} /> False alarm — stand down
          </button>
        </div>
      )}
    </div>
  );
}

// ── Widget ────────────────────────────────────────────────────────────────────

export default function SosWidget({ compact = false }: { compact?: boolean }) {
  useSosKeyframes();
  const [modalOpen, setModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['sos', 'status'],
    queryFn: getSosEligibility,
    refetchInterval: 20_000,
    staleTime: 8_000,
  });

  // Stand-down is the one way an alert can vanish without a responder closing it, so
  // remember the id we cancelled to tell "resolved" apart from "cancelled by me".
  const cancelledIdRef = useRef<number | null>(null);

  const cancelMutation = useMutation({
    mutationFn: (id: number) => cancelSos(id),
    onSuccess: (_res, id) => {
      cancelledIdRef.current = id;
      toast.success('SOS cancelled');
      queryClient.invalidateQueries({ queryKey: ['sos', 'status'] });
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to cancel'),
  });

  const alert = data?.alert ?? null;
  const eligible = data?.eligible ?? false;

  // Tell the raiser the moment a responder picks the alert up or closes it — the bell
  // and the email can lag, and this is the one screen they are watching.
  const prevAlertRef = useRef<{ id: number; status: string } | null>(null);
  useEffect(() => {
    if (!data) return;
    const prev = prevAlertRef.current;
    if (prev && alert && alert.id === prev.id && prev.status === 'open' && alert.status === 'acknowledged') {
      toast.success(`${alert.acknowledged_by_name ?? 'A responder'} acknowledged your SOS — help is on the way`);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
    if (prev && !alert && cancelledIdRef.current !== prev.id) {
      toast.success('Your SOS has been resolved');
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
    prevAlertRef.current = alert ? { id: alert.id, status: alert.status } : null;
  }, [data, alert, queryClient]);

  // Active alert takes precedence over the plain button.
  if (alert) {
    return <ActivePill alert={alert} cancelling={cancelMutation.isPending} onCancel={() => cancelMutation.mutate(alert.id)} />;
  }

  // Only show the button to people with an active booking.
  if (!eligible) return null;

  return (
    <>
      <button type="button" onClick={() => setModalOpen(true)} title="Raise an emergency SOS"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
          font: `700 ${compact ? 12.5 : 13}px ${F}`, color: '#fff',
          background: '#c0392b', border: 'none', borderRadius: 20,
          padding: compact ? '5px 13px' : '7px 16px', cursor: 'pointer',
          transition: 'background .15s, transform .1s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#a52c1f'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#c0392b'; }}
      >
        <Siren size={15} /> SOS
      </button>
      {modalOpen && <SosModal location={data?.location ?? null} onClose={() => setModalOpen(false)} />}
    </>
  );
}
